import { useEffect, useRef, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronRight,
  LogOut,
  Wallet,
  LayoutDashboard,
  MessagesSquare,
  Receipt,
  Store,
  Truck,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';

import Avatar from '@/components/Avatar';
import { useAuthStore, type ManagerRole } from '@/store/auth.store';
import { useProfileSync } from '@/lib/useProfileSync';
import { useViewportBox } from '@/lib/useViewportBox';
import { connectChat, disconnectChat, useChatStore } from '@/lib/chat';
import { disablePush, syncPush } from '@/lib/push';

interface NavItem {
  to: string;
  label: string;
  /** Подпись во вкладке телефона, если полная не влезает в пятую часть ширины. */
  short?: string;
  icon: LucideIcon;
  /** В нижней панели телефона помещается пять вкладок — остальное только в боковом меню. */
  desktopOnly?: boolean;
  /** Вкладка телефона подсвечена и на этих разделах (они открываются переключателем внутри). */
  alsoActive?: string[];
}

const OVERVIEW: NavItem = { to: '/', label: 'Обзор', icon: LayoutDashboard };
const ESTABLISHMENTS: NavItem = { to: '/establishments', label: 'Заведения', icon: Building2 };
// Руководителю — поставщики и оптовики: на компьютере пунктами меню, на телефоне —
// переключателем над списком заведений (ClientsTabs), вкладка «Заведения» остаётся подсвеченной.
const DIRECTOR_ESTABLISHMENTS: NavItem = { ...ESTABLISHMENTS, alsoActive: ['/suppliers', '/wholesalers'] };
const SUPPLIERS: NavItem = { to: '/suppliers', label: 'Поставщики', icon: Truck, desktopOnly: true };
const WHOLESALERS: NavItem = { to: '/wholesalers', label: 'Оптовики', icon: Store, desktopOnly: true };
const MANAGERS: NavItem = { to: '/managers', label: 'Менеджеры', short: 'Команда', icon: Users };
const EXPENSES: NavItem = { to: '/expenses', label: 'Расходы', icon: Receipt };
const CHAT: NavItem = { to: '/chat', label: 'Чат', icon: MessagesSquare };
const PAYOUTS: NavItem = { to: '/payouts', label: 'Выплаты', icon: Wallet };
// У руководителя «Обзор» — отдел целиком, его собственные ставка, начисления и
// выплаты — здесь (на телефоне — через имя в шапке).
const ME: NavItem = { to: '/me', label: 'Личный кабинет', icon: UserRound, desktopOnly: true };

const NAV: Record<ManagerRole, NavItem[]> = {
  MANAGER: [OVERVIEW, ESTABLISHMENTS, CHAT, PAYOUTS],
  HEAD_MANAGER: [OVERVIEW, ESTABLISHMENTS, MANAGERS, CHAT, PAYOUTS],
  SALES_DIRECTOR: [OVERVIEW, DIRECTOR_ESTABLISHMENTS, SUPPLIERS, WHOLESALERS, MANAGERS, EXPENSES, CHAT, ME],
};

const ROLE_TITLE: Record<ManagerRole, string> = {
  MANAGER: 'Кабинет менеджера',
  HEAD_MANAGER: 'Кабинет главного менеджера',
  SALES_DIRECTOR: 'Кабинет руководителя отдела продаж',
};

const ROLE_BADGE: Partial<Record<ManagerRole, string>> = {
  HEAD_MANAGER: 'Главный менеджер',
  SALES_DIRECTOR: 'Руководитель отдела продаж',
};

const ROLE_SHORT: Partial<Record<ManagerRole, string>> = {
  HEAD_MANAGER: 'главный менеджер',
  SALES_DIRECTOR: 'руководитель отдела',
};

function UnreadBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={clsx(
        'inline-flex min-w-[18px] items-center justify-center rounded-full bg-orange px-1 text-[10px] font-semibold leading-[18px] text-white',
        className,
      )}
      aria-label={`${count} непрочитанных`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const manager = useAuthStore((s) => s.manager);
  const logout = useAuthStore((s) => s.logout);
  const unread = useChatStore((s) => s.unread);
  const navigate = useNavigate();
  const location = useLocation();
  useProfileSync();

  const role: ManagerRole = manager?.role ?? 'MANAGER';
  const nav = NAV[role];
  const mobileNav = nav.filter((item) => !item.desktopOnly);

  // Чат — экран мессенджера: от шапки до нижнего меню, страница не скроллится,
  // поле ввода живёт над клавиатурой, меню на это время прячется.
  const chatScreen = location.pathname === CHAT.to;
  const shellRef = useRef<HTMLDivElement>(null);
  const keyboardOpen = useViewportBox(shellRef, chatScreen);

  useEffect(() => {
    connectChat();
    void syncPush();
    return () => disconnectChat();
  }, []);

  // Клик по пушу при открытом кабинете: service worker просит перейти без перезагрузки.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type === 'open-url' && e.data.url?.startsWith('/')) navigate(e.data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  const onLogout = () => {
    void disablePush()
      .catch(() => undefined)
      .finally(logout);
  };

  return (
    <div
      ref={shellRef}
      className={clsx(
        'flex flex-col md:flex-row bg-paper',
        chatScreen ? 'fixed inset-x-0 top-0 h-dvh overflow-hidden overscroll-none' : 'min-h-dvh',
      )}
    >
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-60 shrink-0 flex-col border-r border-line bg-white p-4">
        <div className="mb-6">
          <div className="text-lg font-semibold">Resstro</div>
          <div className="text-xs text-ink/50">{ROLE_TITLE[role]}</div>
        </div>
        <nav className="space-y-1 flex-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm',
                  isActive ? 'bg-orange/10 text-ink font-medium' : 'text-ink/70 hover:bg-paper',
                )
              }
            >
              <item.icon size={16} />
              <span className="flex-1">{item.label}</span>
              {item.to === CHAT.to ? <UnreadBadge count={unread} /> : null}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-line pt-4 text-sm">
          <Link to="/me" className="-mx-2 flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-paper" title="Личный кабинет">
            <Avatar name={manager?.name ?? '?'} url={manager?.avatarUrl} size={36} />
            <div className="min-w-0">
              <div className="truncate font-medium">{manager?.name}</div>
              <div className="text-ink/50 text-xs truncate">{manager?.email}</div>
              {ROLE_BADGE[role] ? (
                <div className="mt-1.5 inline-flex rounded-md bg-orange/10 px-2 py-0.5 text-[11px] font-medium text-orange-700">
                  {ROLE_BADGE[role]}
                </div>
              ) : null}
            </div>
          </Link>
          <button type="button" className="btn-ghost mt-3 w-full !justify-start" onClick={onLogout}>
            <LogOut size={14} /> Выйти
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-20 shrink-0 border-b border-line bg-white/95 backdrop-blur px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 flex items-center justify-between gap-3">
        <Link to="/me" className="flex min-w-0 items-center gap-2.5" aria-label="Личный кабинет">
          <Avatar name={manager?.name ?? '?'} url={manager?.avatarUrl} size={34} />
          <div className="min-w-0">
            <div className="text-base font-semibold leading-tight">Resstro</div>
            <div className="text-[11px] text-ink/50 truncate">
              {manager?.name ?? 'Менеджер'}
              {ROLE_SHORT[role] ? ` · ${ROLE_SHORT[role]}` : ''}
            </div>
          </div>
          <ChevronRight size={14} className="-ml-1 mt-3 shrink-0 text-ink/30" />
        </Link>
        <button type="button" className="btn-ghost !px-2.5 !py-2 shrink-0" onClick={onLogout} aria-label="Выйти">
          <LogOut size={18} />
        </button>
      </header>

      <main
        className={clsx(
          chatScreen
            ? 'flex min-h-0 min-w-0 flex-1 flex-col'
            : 'flex-1 p-4 md:p-8 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-8',
        )}
      >
        {children}
      </main>

      {/* Mobile bottom tabs */}
      <nav
        className={clsx(
          'md:hidden z-20 border-t border-line bg-white/95 backdrop-blur px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] grid gap-1',
          // В чате меню в потоке под полем ввода, а пока открыта клавиатура — скрыто.
          chatScreen ? (keyboardOpen ? 'hidden' : 'relative shrink-0') : 'fixed bottom-0 inset-x-0',
          mobileNav.length >= 5 ? 'grid-cols-5' : mobileNav.length === 4 ? 'grid-cols-4' : 'grid-cols-3',
        )}
      >
        {mobileNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              clsx(
                'relative flex flex-col items-center justify-center gap-0.5 rounded-xl py-2 text-[11px] font-medium min-h-[48px]',
                isActive || item.alsoActive?.some((p) => location.pathname.startsWith(p))
                  ? 'text-orange bg-orange/10'
                  : 'text-ink/55',
              )
            }
          >
            <item.icon size={20} strokeWidth={2} />
            <span className="max-w-full truncate px-0.5">{mobileNav.length >= 5 ? (item.short ?? item.label) : item.label}</span>
            {item.to === CHAT.to ? (
              <UnreadBadge count={unread} className="absolute right-1/2 top-1 translate-x-[18px]" />
            ) : null}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
